import React, { useState, useEffect, useMemo } from 'react';
import {
  Download, Users, MessageSquare, Activity, SlidersHorizontal,
  FlaskConical, Rocket, GraduationCap, Stethoscope, Dna,
  TrendingUp, Image, ShieldCheck, X, Copy, Check,
  Eye, Clapperboard, Wand2, Radar, Zap, Repeat, type LucideIcon
} from 'lucide-react';
import Navbar from '../components/Navbar';

interface StatsData {
  date: string;
  totalRequests: number;
  uniqueIps: number;
  uniqueIpsNoBot: number;
}

interface VersionData {
  win: string | null;
  mac: string | null;
}

// ======== 模型分类：小图标 + 配色（对话 / 图片理解 / 视频生成 / 图片编辑） ========
type ModelKind = 'chat' | 'vision' | 'video' | 'imageEdit';

const MODEL_KINDS: Record<ModelKind, { label: string; icon: LucideIcon; card: string; text: string; chip: string }> = {
  chat: { label: '对话模型', icon: MessageSquare, card: 'from-blue-50 to-cyan-50 border-blue-100', text: 'text-blue-700', chip: 'bg-blue-50 text-blue-600' },
  vision: { label: '图片理解', icon: Eye, card: 'from-sky-50 to-teal-50 border-sky-100', text: 'text-sky-700', chip: 'bg-sky-50 text-sky-600' },
  video: { label: '视频生成', icon: Clapperboard, card: 'from-fuchsia-50 to-pink-50 border-fuchsia-100', text: 'text-fuchsia-700', chip: 'bg-fuchsia-50 text-fuchsia-600' },
  imageEdit: { label: '图片编辑', icon: Wand2, card: 'from-violet-50 to-purple-50 border-violet-100', text: 'text-violet-700', chip: 'bg-violet-50 text-violet-600' },
};

const AI_MODELS: { name: string; kind: ModelKind }[] = [
  // 对话模型
  { name: 'GPT-6 Astra', kind: 'chat' },
  { name: 'Claude Fable 5.1', kind: 'chat' },
  { name: 'Claude Opus 5', kind: 'chat' },
  { name: 'DeepSeek-V4-Pro', kind: 'chat' },
  { name: 'DeepSeek-V4.1-Flash', kind: 'chat' },
  { name: 'GLM-5.3', kind: 'chat' },
  { name: 'GLM-5.2', kind: 'chat' },
  { name: 'GLM-5.3-Flash', kind: 'chat' },
  { name: 'Qwen3.8-Max', kind: 'chat' },
  { name: 'Kimi K3', kind: 'chat' },
  { name: 'Doubao-Seed 2.1', kind: 'chat' },
  // 图片理解
  { name: 'Gemini 3.1 Pro', kind: 'vision' },
  { name: 'GLM-5V-Turbo', kind: 'vision' },
  { name: 'Qwen3.8-Flash', kind: 'vision' },
  // 视频生成
  { name: 'Seedance 2.5', kind: 'video' },
  { name: 'MiniMax H3', kind: 'video' },
  { name: 'Kling 3.0', kind: 'video' },
  { name: 'Wan 3.0', kind: 'video' },
  { name: 'Gemini Omni Flash', kind: 'video' },
  // 图片编辑
  { name: 'Seedream 5.0', kind: 'imageEdit' },
  { name: 'Nano Banana Pro', kind: 'imageEdit' },
  { name: 'GPT Image 2.0', kind: 'imageEdit' },
];

// ======== 数据集场景：跑马灯卡片（新增数据集只需往数组里加一行） ========
const DATASET_SCENES: { icon: LucideIcon; title: string; desc: string; color: string; bg: string; border: string }[] = [
  {
    icon: GraduationCap,
    title: 'LLM 学习 · 后训练',
    desc: '经典语料开箱即用，一晚上训练出你的第一个语言模型',
    color: 'text-violet-600',
    bg: 'bg-violet-50',
    border: 'hover:border-violet-200',
  },
  {
    icon: TrendingUp,
    title: '股票日间数据',
    desc: 'A 股十年日间数据一键加载，即刻开始量化建模',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'hover:border-orange-200',
  },
  {
    icon: Radar,
    title: 'YOLO 目标检测',
    desc: 'COCO8 分钟跑通流程，VisDrone 无人机小目标检测实战',
    color: 'text-cyan-600',
    bg: 'bg-cyan-50',
    border: 'hover:border-cyan-200',
  },
  {
    icon: Stethoscope,
    title: '医疗数据',
    desc: '中文医疗问答数据集，训练一个懂医学的助手',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    border: 'hover:border-rose-200',
  },
  {
    icon: Dna,
    title: '蛋白质序列',
    desc: '40 万条 PDB 蛋白质序列，开箱即用',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'hover:border-emerald-200',
  },
  {
    icon: Image,
    title: '专项图片数据',
    desc: '专项图片数据集，训练一个懂你审美的模型',
    color: 'text-sky-600',
    bg: 'bg-sky-50',
    border: 'hover:border-sky-200',
  },
  {
    icon: ShieldCheck,
    title: '自有数据上传',
    desc: '数据留在本地，模型完全属于你',
    color: 'text-slate-600',
    bg: 'bg-slate-100',
    border: 'hover:border-slate-300',
  },
];

const Home: React.FC = () => {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [version, setVersion] = useState<VersionData | null>(null);
  const [showContact, setShowContact] = useState(false);
  const [copiedAccount, setCopiedAccount] = useState(false);

  const XHS_ACCOUNT = '剪刀手哈哈';

  const handleCopyAccount = async () => {
    try {
      await navigator.clipboard.writeText(XHS_ACCOUNT);
      setCopiedAccount(true);
      setTimeout(() => setCopiedAccount(false), 2000);
    } catch (e) {
      console.error('复制账号失败:', e);
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch('/api/stats/today');
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('获取统计数据失败:', error);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // 获取当前发布版本号（显示在下载按钮旁）
  useEffect(() => {
    fetch('/api/version/latest')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setVersion(data))
      .catch(() => {});
  }, []);

  // 🔥 算力牌价：多卡型 × 多卡数组合（状态随机冻结于本次挂载，避免统计轮询重渲染时乱跳）
  // H200/H100 售罄/紧张概率更高（贴近真实市场：旗舰卡最抢手），多卡数更易紧俏
  const gpuOffers = useMemo<{ gpu: string; count: number; vram: string; price: string; status: 'idle' | 'low' | 'soldout' }[]>(() => {
    const offers = [
      { gpu: 'T4', count: 1, vram: '16G', price: '6.9' },
      { gpu: 'T4', count: 2, vram: '32G', price: '12.9' },
      { gpu: 'T4', count: 4, vram: '64G', price: '24.9' },
      { gpu: 'V100', count: 1, vram: '32G', price: '14.9' },
      { gpu: 'V100', count: 2, vram: '64G', price: '27.9' },
      { gpu: 'V100', count: 4, vram: '128G', price: '52.9' },
      { gpu: 'A10', count: 1, vram: '24G', price: '9.9' },
      { gpu: 'A10', count: 2, vram: '48G', price: '18.5' },
      { gpu: 'A10', count: 4, vram: '96G', price: '35.9' },
      { gpu: 'A100', count: 1, vram: '80G', price: '19.9' },
      { gpu: 'A100', count: 2, vram: '160G', price: '37.9' },
      { gpu: 'A100', count: 4, vram: '320G', price: '72.9' },
      { gpu: 'A100', count: 8, vram: '640G', price: '139' },
      { gpu: 'H100', count: 1, vram: '80G', price: '31.9' },
      { gpu: 'H100', count: 2, vram: '160G', price: '61.9' },
      { gpu: 'H100', count: 4, vram: '320G', price: '119' },
      { gpu: 'H100', count: 8, vram: '640G', price: '229' },
      { gpu: 'H200', count: 1, vram: '141G', price: '48.9' },
      { gpu: 'H200', count: 2, vram: '282G', price: '94.9' },
      { gpu: 'H200', count: 4, vram: '564G', price: '183' },
      { gpu: 'H200', count: 8, vram: '1128G', price: '355' },
    ];
    const roll = (o: { gpu: string; count: number }): 'idle' | 'low' | 'soldout' => {
      // 旗舰卡紧俏概率：H200 售罄 45% / 紧张 35%；H100 售罄 35% / 紧张 35%；其余基本空闲
      const isH200 = o.gpu === 'H200';
      const isH100 = o.gpu === 'H100';
      const soldoutP = isH200 ? 0.45 : isH100 ? 0.35 : 0.04;
      const lowP = (isH200 || isH100) ? 0.35 : 0.15;
      const r = Math.random();
      if (r < soldoutP) return 'soldout';
      if (r < soldoutP + lowP) return 'low';
      return 'idle';
    };
    // 保证 H 系至少各有一个售罄档（旗舰稀缺感），除非随机全空
    let result = offers.map(o => ({ ...o, status: roll(o) }));
    for (const gpu of ['H200', 'H100']) {
      if (!result.some(o => o.gpu === gpu && o.status === 'soldout')) {
        const candidates = result.filter(o => o.gpu === gpu);
        if (candidates.length > 0) {
          const pick = candidates[Math.floor(Math.random() * candidates.length)];
          pick.status = 'soldout';
        }
      }
    }
    return result;
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <Navbar />

      {/* ======== Hero Section ======== */}
      <header className="relative pt-44 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          {/* 大面积柔和光晕 */}
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-[radial-gradient(ellipse_at_center,_rgba(139,92,246,0.18)_0%,_transparent_70%)]" />
          <div className="absolute top-20 -left-20 w-[400px] h-[400px] bg-[radial-gradient(circle,_rgba(59,130,246,0.12)_0%,_transparent_70%)]" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-14 text-slate-800">
            一句话，
            <span className="bg-gradient-to-r from-violet-600 via-fuchsia-500 to-violet-600 bg-clip-text text-transparent">
              Teegal
            </span>
            帮你自动训练模型
          </h1>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <a
              href="/downloads/teegal-setup.exe"
              className="group inline-flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-medium transition-all duration-300 shadow-lg shadow-slate-500/20 hover:shadow-slate-500/30 hover:scale-105"
            >
              <Download size={18} />
              <span>Windows 版{version?.win ? ` v${version.win}` : ''}</span>
            </a>
            <a
              href="/downloads/Teegal-mac.dmg"
              className="group inline-flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-medium transition-all duration-300 shadow-lg shadow-slate-500/20 hover:shadow-slate-500/30 hover:scale-105"
            >
              <Download size={18} />
              <span>macOS 版{version?.mac ? ` v${version.mac}` : ''}</span>
            </a>
          </div>

          {stats && stats.totalRequests > 0 && (
            <div className="flex items-center justify-center gap-2 text-slate-500">
              <Users size={16} className="text-violet-500" />
              <span>
                今日 <strong className="text-violet-600 font-semibold">{(stats.totalRequests + 1).toLocaleString()}</strong> 次访问
              </span>
            </div>
          )}
        </div>
      </header>

      {/* ======== 核心数字 ======== */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-3 gap-4">
            {[
              { num: '10+', label: '10+ 原生场景数据集', color: 'from-violet-500 to-fuchsia-500' },
              { num: '8/30', label: '8大卡系，30+数据中心算力网络', color: 'from-emerald-500 to-teal-600' },
              { num: '∞', label: '节约千万算法部', color: 'from-orange-500 to-amber-600' },
            ].map((item, i) => (
              <div key={i} className="text-center p-4 sm:p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-100">
                <div className={`text-2xl sm:text-4xl font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent mb-2 whitespace-nowrap`}>
                  {item.num}
                </div>
                <div className="text-sm text-slate-500">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== 零门槛五步 ======== */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50/50 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 text-slate-800">
              从一句话，到你的模型上线
            </h2>

            {/* 被划掉的偏见 */}
            <div className="text-slate-400 text-sm sm:text-base">
              <span className="relative inline-block">
                千万年薪构建算法部
                <span className="strike-line absolute left-[-2%] right-[-2%] top-[calc(50%_-_1px)] h-[2px] rounded-full bg-gradient-to-r from-rose-500 to-red-400" />
              </span>
            </div>

            {/* 弯曲箭头：从划掉的话指向真正的答案 */}
            <svg width="40" height="28" viewBox="0 0 44 30" fill="none" className="mx-auto my-1.5 text-rose-400">
              <path
                d="M34 2 C 36 10, 30 16, 22 19 C 18 20.5, 15 21.5, 15 26"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                pathLength={1}
                className="arrow-draw"
                style={{ animationDelay: '1s' }}
              />
              <path
                d="M10.8 21.8 L15 26.5 L19.2 21.8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                pathLength={1}
                className="arrow-draw"
                style={{ animationDelay: '1.45s' }}
              />
            </svg>

            <p className="text-slate-700 font-medium leading-relaxed">
              无需写代码 · 无需懂算法 · 无需买显卡
            </p>
          </div>

          <style>{`
            .strike-line {
              transform: scaleX(0) rotate(-1.5deg);
              transform-origin: left center;
              animation: strikeIn 0.6s ease-out 0.4s forwards;
            }
            @keyframes strikeIn {
              to { transform: scaleX(1) rotate(-1.5deg); }
            }
            .arrow-draw {
              stroke-dasharray: 1;
              stroke-dashoffset: 1;
              animation: drawIn 0.45s ease-out forwards;
            }
            @keyframes drawIn {
              to { stroke-dashoffset: 0; }
            }
          `}</style>

          {/* 时间线 */}
          <div className="relative">
            {/* 连接线（桌面横向） */}
            <div className="hidden md:block absolute top-10 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-violet-200 via-fuchsia-200 to-orange-200" />
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-3 relative">

              {[
                {
                  step: '01',
                  icon: MessageSquare,
                  title: '一句话开始',
                  desc: '说出需求，环境自动就绪',
                  color: 'from-violet-500 to-purple-600',
                  bg: 'from-violet-50 to-white',
                  border: 'border-violet-100',
                  iconColor: 'text-violet-600',
                },
                {
                  step: '02',
                  icon: Activity,
                  title: '实时看结果',
                  desc: '训练进度与曲线全程可见',
                  color: 'from-fuchsia-500 to-pink-500',
                  bg: 'from-fuchsia-50 to-white',
                  border: 'border-fuchsia-100',
                  iconColor: 'text-fuchsia-600',
                },
                {
                  step: '03',
                  icon: SlidersHorizontal,
                  title: '动态调整',
                  desc: '参数、数据随时可改',
                  color: 'from-blue-500 to-cyan-500',
                  bg: 'from-blue-50 to-white',
                  border: 'border-blue-100',
                  iconColor: 'text-blue-600',
                },
                {
                  step: '04',
                  icon: FlaskConical,
                  title: '测试调优',
                  desc: '基准测试自动评估效果',
                  color: 'from-emerald-500 to-teal-600',
                  bg: 'from-emerald-50 to-white',
                  border: 'border-emerald-100',
                  iconColor: 'text-emerald-600',
                },
                {
                  step: '05',
                  icon: Rocket,
                  title: '一键发布',
                  desc: '云端部署，链接即刻调用',
                  color: 'from-orange-500 to-amber-500',
                  bg: 'from-orange-50 to-white',
                  border: 'border-orange-100',
                  iconColor: 'text-orange-600',
                },
              ].map((item, i) => (
                <div key={i} className="text-center group">
                  {/* 圆形图标节点 */}
                  <div className={`relative mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br ${item.bg} border ${item.border} flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg`}>
                    <item.icon size={30} className={item.iconColor} />
                    <div className={`absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gradient-to-br ${item.color} text-white text-[10px] font-bold flex items-center justify-center shadow`}>
                      {i + 1}
                    </div>
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-1.5">{item.title}</h3>
                  <p className="text-xs sm:text-sm text-slate-500 leading-relaxed px-1">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ======== 700+ 模型接入 ======== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-3 text-slate-800">700+ 模型任你差遣</h2>
          <p className="text-slate-500 mb-8 leading-relaxed max-w-2xl mx-auto">
            国内外主流大模型开箱即用，你的模型训练好了，还能和它们同台比一比。
          </p>

          {/* 模型跑马灯：一次约 4 张，无缝循环滚动（悬停暂停）
              每张卡片用固定 mr-3 尾边距代替容器 gap，保证 translateX(-50%) 恰好等于一份列表宽度，实现真正无缝 */}
          <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
            <div className="flex w-max animate-[marquee_52s_linear_infinite] hover:[animation-play-state:paused]">
              {[...AI_MODELS, ...AI_MODELS].map((model, i) => {
                const k = MODEL_KINDS[model.kind];
                return (
                  <div
                    key={`${model.name}-${i}`}
                    className={`shrink-0 w-52 mr-3 px-3 py-2.5 rounded-xl text-center border bg-gradient-to-r ${k.card}`}
                  >
                    <div className={`flex items-center justify-center gap-1.5 text-sm font-medium ${k.text}`}>
                      <k.icon size={14} className="shrink-0" />
                      <span className="truncate">{model.name}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-center gap-1.5 text-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <span className={k.text}>{k.label}</span>
                      <span className="text-slate-400">· 可用</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 图例：四类模型的图标说明 */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {(Object.keys(MODEL_KINDS) as ModelKind[]).map((kind) => {
              const k = MODEL_KINDS[kind];
              const Icon = k.icon;
              return (
                <span key={kind} className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-md ${k.chip}`}>
                    <Icon size={12} />
                  </span>
                  {k.label}
                </span>
              );
            })}
          </div>

          <style>{`
            @keyframes marquee {
              from { transform: translateX(0); }
              to { transform: translateX(-50%); }
            }
          `}</style>
        </div>
      </section>

      {/* ======== 丰富的数据集 ======== */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 text-slate-800">
              丰富的数据集
            </h2>
            <p className="text-slate-500 leading-relaxed max-w-2xl mx-auto">
              对话即热启动，一步进入研究状态
            </p>
          </div>

          {/* 数据集场景跑马灯：一次约 3 张，无缝循环滚动（悬停暂停），复用上方 marquee 动画
              卡片用固定 mr-4 尾边距保证 translateX(-50%) 恰好等于一份列表宽度，实现真正无缝 */}
          <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
            <div className="flex w-max items-stretch animate-[marquee_36s_linear_infinite] hover:[animation-play-state:paused]">
              {[...DATASET_SCENES, ...DATASET_SCENES].map((item, i) => (
                <div
                  key={`${item.title}-${i}`}
                  className={`shrink-0 w-80 mr-4 p-6 rounded-2xl bg-white border border-slate-100 ${item.border} transition-all duration-300 hover:shadow-lg`}
                >
                  <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl ${item.bg} mb-4`}>
                    <item.icon size={22} className={item.color} />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={() => setShowContact(true)}
            className="mx-auto mt-8 flex items-center justify-center gap-1.5 text-sm text-slate-400 hover:text-emerald-600 transition-colors"
          >
            您有数据环境？欢迎联系合作接入
          </button>
        </div>
      </section>

      {/* ======== 数据主权 ======== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50/50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 mb-4">
              <ShieldCheck size={26} className="text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold mb-3 text-slate-800">你的数据，你的模型</h2>
            <p className="text-slate-500 leading-relaxed max-w-xl mx-auto">
              数据存在本地，模型完全属于你。
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: '数据不出门', desc: '留在本地，不经第三方' },
              { title: '模型归你所有', desc: '随时导出，随意部署' },
              { title: '想用云端也行', desc: '一键上云，按需计费' },
            ].map((item, i) => (
              <div key={i} className="p-5 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm text-center">
                <h3 className="font-semibold text-slate-800 mb-1">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== 循环研究 ======== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-3 text-slate-800">
            递归+弹性
          </h2>


          {/* ---- 子模块一：弹性算力网络 ---- */}
          <h3 className="text-base font-semibold text-slate-800 mb-1 inline-flex items-center gap-1.5">
            <Zap size={16} className="text-orange-500" />
            弹性算力网络
          </h3>
          <p className="text-sm text-slate-400 mb-4">
            覆盖 30+ 数据中心，H200 / H100 / A100 / V100 / A10 / T4 按需调度
          </p>

          {/* 算力牌价：可滚动列表（21 个档位：T4/V100/A10/A100/H100/H200 × 1/2/4/8 卡） */}
          <div className="max-w-3xl mx-auto mb-4">
            <div className="rounded-2xl border border-orange-100 bg-white overflow-hidden">
              {/* 表头：四列均分（各占 1/4），状态列居中、价格右对齐 */}
              <div className="grid grid-cols-4 items-center px-4 sm:px-6 py-2.5 bg-gradient-to-r from-orange-50 to-amber-50 text-xs text-orange-500">
                <span>卡型</span>
                <span>显存</span>
                <span className="text-center">状态</span>
                <span className="text-right">指引价</span>
              </div>
              {/* 可滚动的价格列表 */}
              <div className="max-h-56 overflow-y-auto divide-y divide-orange-50/70 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-orange-200">
                {gpuOffers.map(({ gpu, count, vram, price, status }) => {
                  const statusUI = {
                    idle: { label: '空闲', dot: 'bg-emerald-500', text: 'text-emerald-600' },
                    low: { label: '紧张', dot: 'bg-amber-500', text: 'text-amber-600' },
                    soldout: { label: '售罄', dot: 'bg-slate-400', text: 'text-slate-400' },
                  }[status];
                  return (
                    <div
                      key={`${gpu}x${count}`}
                      className={`grid grid-cols-4 items-center px-4 sm:px-6 py-2.5 ${status === 'soldout' ? 'opacity-50' : ''}`}
                    >
                      <span className="text-sm font-semibold text-orange-700">
                        {gpu} <span className="text-slate-400 font-normal">×{count}</span>
                      </span>
                      <span className="text-xs text-slate-400">{vram}</span>
                      <span className={`flex items-center justify-center gap-1.5 text-xs ${statusUI.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusUI.dot}`} />
                        {statusUI.label}
                      </span>
                      <span className="text-right text-sm font-medium text-slate-700">
                        ¥{price}
                        <span className="text-xs text-slate-400 font-normal">/时</span>
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* 滚动到底：资源合作入口 */}
              <button
                onClick={() => setShowContact(true)}
                className="w-full flex items-center justify-center gap-2 py-3 border-t border-orange-100 text-sm text-emerald-600 hover:bg-emerald-50 transition-colors"
              >
                您有卡资源，欢迎联系合作接入
              </button>
            </div>
          </div>
          <p className="text-sm text-slate-400 mb-10">6 大卡系 21 档算力 · 实时状态 · 弹性供给</p>

          {/* ---- 子模块二：递归式循环研究 ---- */}
          <h3 className="text-base font-semibold text-slate-800 mb-1 inline-flex items-center gap-1.5">
            <Repeat size={16} className="text-orange-500" />
            递归式循环研究
          </h3>
          <p className="text-sm text-slate-400 mb-6">
            Agent 自主探索、验证、优化，24 小时自动运转，无需人工干预
          </p>

          {/* 循环图示 */}
          <div className="relative max-w-md mx-auto mb-8">
            <svg viewBox="0 0 300 160" className="w-full h-auto">
              {/* 定义动画 */}
              <defs>
                <linearGradient id="flowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#f97316" stopOpacity="0" />
                  <stop offset="50%" stopColor="#f97316" stopOpacity="1" />
                  <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
                </linearGradient>
                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#f97316" />
                </marker>
              </defs>

              {/* 循环箭头背景 */}
              <path
                d="M 30 80 Q 30 30 150 30 Q 270 30 270 80 Q 270 130 150 130 Q 30 130 30 80"
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="3"
                strokeDasharray="8 4"
              />

              {/* 流动动画路径 - 使用CSS动画 */}
              <path
                className="flow-path"
                d="M 30 80 Q 30 30 150 30 Q 270 30 270 80 Q 270 130 150 130 Q 30 130 30 80"
                fill="none"
                stroke="#f97316"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="20 200"
              />

              {/* 流动的点 */}
              <circle className="flow-dot" r="4" fill="#f97316">
                <animateMotion
                  dur="3s"
                  repeatCount="indefinite"
                  path="M 30 80 Q 30 30 150 30 Q 270 30 270 80 Q 270 130 150 130 Q 30 130 30 80"
                />
              </circle>

              {/* 节点 */}
              {/* 探索 */}
              <circle cx="30" cy="80" r="22" fill="white" stroke="#f97316" strokeWidth="2" className="flow-node" />
              <text x="30" y="76" textAnchor="middle" className="text-xs font-medium" fill="#f97316">探索</text>
              <text x="30" y="88" textAnchor="middle" className="text-[9px]" fill="#64748b">Explore</text>

              {/* 验证 */}
              <circle cx="150" cy="30" r="22" fill="white" stroke="#f97316" strokeWidth="2" className="flow-node" />
              <text x="150" y="26" textAnchor="middle" className="text-xs font-medium" fill="#f97316">验证</text>
              <text x="150" y="38" textAnchor="middle" className="text-[9px]" fill="#64748b">Verify</text>

              {/* 优化 */}
              <circle cx="270" cy="80" r="22" fill="white" stroke="#f97316" strokeWidth="2" className="flow-node" />
              <text x="270" y="76" textAnchor="middle" className="text-xs font-medium" fill="#f97316">优化</text>
              <text x="270" y="88" textAnchor="middle" className="text-[9px]" fill="#64748b">Optimize</text>

              {/* 迭代 */}
              <circle cx="150" cy="130" r="22" fill="white" stroke="#f97316" strokeWidth="2" className="flow-node" />
              <text x="150" y="126" textAnchor="middle" className="text-xs font-medium" fill="#f97316">迭代</text>
              <text x="150" y="138" textAnchor="middle" className="text-[9px]" fill="#64748b">Iterate</text>

              {/* 中心文字 */}
              <text x="150" y="78" textAnchor="middle" className="text-[10px]" fill="#94a3b8">持续优化</text>
              <text x="150" y="92" textAnchor="middle" className="text-[10px]" fill="#94a3b8">直到最优</text>
            </svg>
          </div>

          {/* 详细说明 */}
          <div className="flex flex-wrap justify-center gap-4 max-w-2xl mx-auto">
            {[
              { title: '探索', desc: '搜索问题空间' },
              { title: '验证', desc: '确保准确性' },
              { title: '优化', desc: '持续改进' },
              { title: '迭代', desc: '循环收敛' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-full border border-orange-100">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold text-xs">
                  {i + 1}
                </div>
                <span className="text-sm font-medium text-slate-700">{item.title}</span>
                <span className="text-sm text-slate-400">{item.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======== Download Section ======== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-semibold mb-3 text-slate-800">开始训练出你的第一个模型</h2>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="/downloads/teegal-setup.exe"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 rounded-xl text-white transition-colors"
            >
              <Download size={18} />
              <span>Windows</span>
            </a>
            <a
              href="/downloads/Teegal-mac.dmg"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 rounded-xl text-white transition-colors"
            >
              <Download size={18} />
              <span>macOS</span>
            </a>
          </div>

          {/* 联系入口：定制自主的 Agent */}
          <div className="mt-8 flex flex-col items-center gap-2">
            <p className="text-sm text-slate-500">想定制属于你的自主 Agent？联系我们</p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowContact(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-white border border-slate-200 hover:border-green-400 hover:bg-green-50 rounded-full text-sm text-slate-700 transition-colors"
              >
                {/* 微信图标 */}
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#22c55e">
                  <path d="M8.69 3.5C4.98 3.5 2 6.05 2 9.2c0 1.77.96 3.35 2.46 4.4l-.62 1.85 2.16-1.08c.55.15 1.13.24 1.72.26a5.54 5.54 0 0 1-.1-1.05c0-3.03 2.89-5.5 6.44-5.5.22 0 .43.01.64.03C14.06 5.5 11.63 3.5 8.69 3.5zM6.6 7.3a.82.82 0 1 1 0 1.63.82.82 0 0 1 0-1.63zm4.24 0a.82.82 0 1 1 0 1.63.82.82 0 0 1 0-1.63zM15.06 9c-3.17 0-5.75 2.14-5.75 4.78 0 2.63 2.58 4.77 5.75 4.77.48 0 .95-.05 1.4-.15l1.93.97-.55-1.66c1.34-.9 2.21-2.28 2.21-3.93C20.05 11.14 18.06 9 15.06 9zm-2.06 2.4a.72.72 0 1 1 0 1.44.72.72 0 0 1 0-1.44zm4.12 0a.72.72 0 1 1 0 1.44.72.72 0 0 1 0-1.44z"/>
                </svg>
                <span>微信</span>
              </button>
              <button
                onClick={() => setShowContact(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-white border border-slate-200 hover:border-red-400 hover:bg-red-50 rounded-full text-sm text-slate-700 transition-colors"
              >
                {/* 小红书图标 */}
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#ef4444">
                  <path d="M4 4.5h16a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2zm3.3 3.2v1.6h2.2c-.05.7-.3 1.35-.75 1.95-.45.6-1.15 1.15-2.1 1.6l.85 1.35c.85-.45 1.55-1 2.05-1.65.15.3.4.6.7.9.55.55 1.25.75 2.1.75h4.35V8.3h-2.1v4.15h-2c-.45 0-.8-.1-1.05-.4a1.9 1.9 0 0 1-.4-.8c.4-.75.6-1.55.6-2.45h-2.1v-.6h2.55V7.1H9.4v.6H7.3zM5.5 14.9v1.5h6.1v-1.5H5.5zm8.2 0v1.5h4.8v-1.5h-4.8z"/>
                </svg>
                <span>小红书</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 联系方式弹窗 */}
      {showContact && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowContact(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-sm w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowContact(false)}
              className="absolute top-3 right-3 p-1 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X size={20} />
            </button>

            <h3 className="text-lg font-semibold text-slate-800 mb-4 text-center">联系我们</h3>

            {/* 微信：二维码 */}
            <div className="flex flex-col items-center mb-5">
              <div className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#22c55e">
                  <path d="M8.69 3.5C4.98 3.5 2 6.05 2 9.2c0 1.77.96 3.35 2.46 4.4l-.62 1.85 2.16-1.08c.55.15 1.13.24 1.72.26a5.54 5.54 0 0 1-.1-1.05c0-3.03 2.89-5.5 6.44-5.5.22 0 .43.01.64.03C14.06 5.5 11.63 3.5 8.69 3.5zM6.6 7.3a.82.82 0 1 1 0 1.63.82.82 0 0 1 0-1.63zm4.24 0a.82.82 0 1 1 0 1.63.82.82 0 0 1 0-1.63zM15.06 9c-3.17 0-5.75 2.14-5.75 4.78 0 2.63 2.58 4.77 5.75 4.77.48 0 .95-.05 1.4-.15l1.93.97-.55-1.66c1.34-.9 2.21-2.28 2.21-3.93C20.05 11.14 18.06 9 15.06 9zm-2.06 2.4a.72.72 0 1 1 0 1.44.72.72 0 0 1 0-1.44zm4.12 0a.72.72 0 1 1 0 1.44.72.72 0 0 1 0-1.44z"/>
                </svg>
                微信扫一扫
              </div>
              <img
                src="/images/wechat-qr.png"
                alt="微信二维码"
                className="w-48 h-48 rounded-lg border border-slate-100"
              />
            </div>

            {/* 小红书：账号名 */}
            <div className="flex flex-col items-center">
              <div className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#ef4444">
                  <path d="M4 4.5h16a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2zm3.3 3.2v1.6h2.2c-.05.7-.3 1.35-.75 1.95-.45.6-1.15 1.15-2.1 1.6l.85 1.35c.85-.45 1.55-1 2.05-1.65.15.3.4.6.7.9.55.55 1.25.75 2.1.75h4.35V8.3h-2.1v4.15h-2c-.45 0-.8-.1-1.05-.4a1.9 1.9 0 0 1-.4-.8c.4-.75.6-1.55.6-2.45h-2.1v-.6h2.55V7.1H9.4v.6H7.3zM5.5 14.9v1.5h6.1v-1.5H5.5zm8.2 0v1.5h4.8v-1.5h-4.8z"/>
                </svg>
                小红书搜索
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-lg">
                <span className="text-base font-semibold text-slate-800">{XHS_ACCOUNT}</span>
                <button
                  onClick={handleCopyAccount}
                  className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                  title="复制账号名"
                >
                  {copiedAccount ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 循环流动动画样式 */}
      <style>{`
        .flow-path {
          animation: flowDash 2s linear infinite;
        }
        @keyframes flowDash {
          to {
            stroke-dashoffset: -220;
          }
        }
        .flow-dot {
          animation: dotPulse 1.5s ease-in-out infinite;
        }
        @keyframes dotPulse {
          0%, 100% { r: 3; opacity: 1; }
          50% { r: 5; opacity: 0.7; }
        }
        .flow-node {
          animation: nodeGlow 2s ease-in-out infinite;
        }
        @keyframes nodeGlow {
          0%, 100% { stroke-width: 2; }
          50% { stroke-width: 3; }
        }
      `}</style>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-slate-100">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm text-slate-400">
            © 2026 workbees.space. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
